"use client";

import { useEffect, useRef } from "react";
import { toast as sonnerToast } from "sonner";

const DEPLOYMENT_CHECK_URL = "https://latest-deployment-check.sean-430.workers.dev";
const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
const STORAGE_KEY_PREFIX = "current_deployment_id";
const DISMISS_KEY = "deployment_update_dismissed_until";
const DISMISS_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours
const TOAST_ID = "deploy-update-available";
const RETRY_ATTEMPTS = 3;
const RETRY_DELAY_MS = 5000;

function getProjectSlug(): string {
  return process.env.NEXT_PUBLIC_PROJECT_SLUG || "b3os-web";
}

type DeploymentResponse = {
  deploymentId: string;
  timestamp?: string;
  environment?: string;
  project?: string;
};

async function fetchWithRetry(url: string, attempts = RETRY_ATTEMPTS): Promise<Response> {
  try {
    return await fetch(url, { method: "GET", mode: "cors" });
  } catch (err) {
    if (attempts > 1) {
      await new Promise(r => setTimeout(r, RETRY_DELAY_MS));
      return fetchWithRetry(url, attempts - 1);
    }
    throw err;
  }
}

function showUpdateToast() {
  try {
    const dismissedUntilRaw = localStorage.getItem(DISMISS_KEY);
    const dismissedUntil = dismissedUntilRaw ? Number(dismissedUntilRaw) : 0;
    if (dismissedUntil > Date.now()) return;

    sonnerToast(
      <div className="flex w-full items-center justify-between gap-4">
        <div className="flex flex-col gap-0.5">
          <span className="text-sm font-semibold">Update available</span>
          <span className="text-ds-text-muted text-xs">A new version of the app is available</span>
        </div>
        <button
          onClick={() => {
            try {
              window.location.reload();
            } catch {
              window.location.assign(window.location.href);
            }
          }}
          className="bg-ds-surface-brand hover:bg-ds-surface-brand-hover flex shrink-0 items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium text-white transition-colors"
        >
          Refresh
        </button>
      </div>,
      {
        id: TOAST_ID,
        position: "bottom-left",
        dismissible: true,
        duration: Infinity,
        onDismiss: () => {
          try {
            localStorage.setItem(DISMISS_KEY, String(Date.now() + DISMISS_DURATION_MS));
          } catch {}
        },
      },
    );
  } catch (error) {
    console.error("Failed to show update toast:", error);
    if (window.confirm("A new version is available. Reload now?")) {
      window.location.reload();
    }
  }
}

/**
 * Polls a Cloudflare Worker for the latest Railway deployment ID and shows a
 * toast when a new deploy is detected. Ported from `apps/minimal-app`'s
 * `SyncDeployments` component.
 *
 * The CF Worker receives Railway deploy webhooks at:
 *   POST https://latest-deployment-check.sean-430.workers.dev/webhook?project=b3os-web
 *
 * Setup: add the webhook URL above to the b3os-web Railway service's deploy
 * notifications (Settings → Webhooks → Deploy Success).
 */
export function DeployUpdateChecker(): null {
  const initializedRef = useRef(false);
  const storageKey = `${STORAGE_KEY_PREFIX}:${getProjectSlug()}`;

  useEffect(() => {
    if (process.env.NODE_ENV !== "production") return;
    if (initializedRef.current) return;
    initializedRef.current = true;

    const check = async (isInitial = false) => {
      try {
        const url = `${DEPLOYMENT_CHECK_URL}?project=${encodeURIComponent(getProjectSlug())}`;
        const response = await fetchWithRetry(url);
        if (!response.ok) return;
        const latest = (await response.json()) as DeploymentResponse;
        if (!latest?.deploymentId) return;

        const currentId = localStorage.getItem(storageKey);
        if (isInitial || !currentId) {
          localStorage.setItem(storageKey, latest.deploymentId);
        } else if (latest.deploymentId !== currentId) {
          showUpdateToast();
        }
      } catch (e) {
        console.error("Failed to check for deployment update:", e);
      }
    };

    check(true);
    const intervalId = window.setInterval(() => check(), POLL_INTERVAL_MS);
    return () => window.clearInterval(intervalId);
  }, [storageKey]);

  return null;
}
